import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { UserCog, Plus, X, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const ROLE_LABEL = {
  admin: "Administrador",
  profesional: "Profesional",
  recepcionista: "Recepción",
};

const COLORS = ["#6B8E6B", "#C06C5B", "#6082B6", "#D4A373", "#8E7B6B", "#A67BC0"];

export default function Profesionales() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/users");
      setUsers(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  useEffect(() => {
    load();
  }, []);

  const del = async (id) => {
    if (!confirm("¿Eliminar este usuario?")) return;
    try {
      await api.delete(`/users/${id}`);
      toast.success("Usuario eliminado");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="px-6 md:px-10 lg:px-14 py-10 md:py-14 pb-24 md:pb-14 max-w-[1400px]">
      <PageHeader
        eyebrow="Equipo"
        title="Profesionales & usuarios"
        description="Administrá el acceso de profesionales, recepción y administradores."
        action={
          user?.role === "admin" && (
            <button data-testid="nuevo-usuario-btn" onClick={() => setModalOpen(true)} className="btn-primary-pill">
              <Plus className="w-4 h-4 mr-1.5" /> Nuevo usuario
            </button>
          )
        }
      />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {users.map((u) => (
          <div key={u.id} className="card-clinic p-6 relative" data-testid={`usuario-card-${u.id}`}>
            <div className="flex items-start gap-4">
              <div
                className="w-12 h-12 rounded-full flex items-center justify-center text-white font-semibold shrink-0"
                style={{ backgroundColor: u.color || "#6B8E6B" }}
              >
                {(u.name || "?").split(" ").map((x) => x[0]).slice(0, 2).join("")}
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-bold text-neutral-900 tracking-tight truncate">{u.name}</div>
                <div className="text-xs text-neutral-500 truncate">{u.email}</div>
                <span className="mt-2 inline-block text-xs font-medium px-2.5 py-0.5 rounded-full bg-neutral-100 text-neutral-700">
                  {ROLE_LABEL[u.role] || u.role}
                </span>
              </div>
            </div>
            {user?.role === "admin" && u.id !== user.id && (
              <button
                data-testid={`usuario-eliminar-${u.id}`}
                onClick={() => del(u.id)}
                className="absolute top-3 right-3 text-neutral-400 hover:text-[hsl(var(--terracotta))] transition-colors"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
        ))}
      </div>

      {modalOpen && (
        <UserModal
          onClose={() => setModalOpen(false)}
          onSaved={() => {
            setModalOpen(false);
            load();
          }}
        />
      )}
    </div>
  );
}

function UserModal({ onClose, onSaved }) {
  const [form, setForm] = useState({
    email: "",
    password: "",
    name: "",
    role: "profesional",
    color: COLORS[0],
  });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.email || !form.password || !form.name) {
      toast.error("Completá todos los campos");
      return;
    }
    setLoading(true);
    try {
      await api.post("/users", form);
      toast.success("Usuario creado");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-neutral-900/40 backdrop-blur-sm">
      <div className="bg-white rounded-2xl w-full max-w-lg border border-neutral-200 shadow-xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-200">
          <h3 className="text-lg font-bold tracking-tight">Nuevo usuario</h3>
          <button data-testid="user-modal-close" onClick={onClose} className="btn-ghost-pill p-2">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1.5">Nombre completo</label>
            <input data-testid="user-nombre" className="input-clinic" value={form.name} onChange={(e) => set("name", e.target.value)} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1.5">Email</label>
              <input data-testid="user-email" type="email" className="input-clinic" value={form.email} onChange={(e) => set("email", e.target.value)} />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1.5">Contraseña</label>
              <input data-testid="user-password" type="password" className="input-clinic" value={form.password} onChange={(e) => set("password", e.target.value)} />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1.5">Rol</label>
            <select data-testid="user-role" className="input-clinic" value={form.role} onChange={(e) => set("role", e.target.value)}>
              <option value="profesional">Profesional</option>
              <option value="recepcionista">Recepción</option>
              <option value="admin">Administrador</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">Color identificador</label>
            <div className="flex gap-2">
              {COLORS.map((c) => (
                <button
                  key={c}
                  onClick={() => set("color", c)}
                  data-testid={`user-color-${c}`}
                  className={`w-8 h-8 rounded-full transition-transform ${form.color === c ? "ring-2 ring-neutral-800 ring-offset-2 scale-110" : ""}`}
                  style={{ backgroundColor: c }}
                />
              ))}
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 px-6 py-4 border-t border-neutral-200 bg-neutral-50/50 rounded-b-2xl">
          <button onClick={onClose} className="btn-outline-pill">
            Cancelar
          </button>
          <button data-testid="user-guardar" onClick={save} disabled={loading} className="btn-primary-pill">
            {loading ? "Guardando…" : "Guardar"}
          </button>
        </div>
      </div>
    </div>
  );
}
